import * as XLSX from 'xlsx';

// US State abbreviation to full name mapping for Airwallex
export const US_STATE_ABBREVIATION_TO_NAME: Record<string, string> = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AS': 'American Samoa',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'DC': 'District of Columbia',
  'FL': 'Florida',
  'GA': 'Georgia',
  'GU': 'Guam',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'MP': 'Northern Mariana Islands',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'PR': 'Puerto Rico',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UM': 'United States Minor Outlying Islands',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VI': 'Virgin Islands, U.S.',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming',
};

/**
 * Converts a US state abbreviation to full name for Airwallex
 * If the input is already a full name or not found, returns the input as-is
 */
export function getStateFullName(stateAbbreviation: string): string {
  if (!stateAbbreviation) return '';
  const upperAbbr = stateAbbreviation.toUpperCase().trim();
  return US_STATE_ABBREVIATION_TO_NAME[upperAbbr] || stateAbbreviation;
}

export interface AirwallexTransferRow {
  transferTo: string;
  transferMethod: string;
  currencyRecipientGets: string;
  currencyYouPay: string;
  amount: number;
  feePaidBy: string;
  accountName: string;
  bankAccountType: string;
  bankIdentifierType: string;
  bankIdentifierValue: string;
  accountNumber: string;
  transferPurpose: string;
  reference: string;
  description: string;
  recipientType: string;
  countryRegion: string;
  streetAddress: string;
  city: string;
  stateProvince: string;
  postcode: string;
  requestId?: string;
}

/**
 * Generates an Airwallex batch transfer XLSX file with proper format
 * including the main data sheet and 4 hidden validation sheets.
 * Matches the official Airwallex batch transfer template format.
 */
export function generateAirwallexXLSX(
  transfers: AirwallexTransferRow[],
  batchDate: Date = new Date()
): Blob {
  // Create workbook
  const wb = XLSX.utils.book_new();

  // Create main data sheet with headers
  const headers = [
    'Transfer to',
    'Transfer method',
    'Currency recipient gets',
    'Currency you pay',
    'Transfer amount in currency you pay',
    'Fee paid by',
    'Account name',
    'Bank account type',
    'Bank identifier type',
    'Bank identifier value',
    'Account number',
    'Transfer purpose',
    'Reference',
    'Description (optional)',
    'Recipient type',
    'Country / region',
    'Street address',
    'City',
    'State / Province',
    'Postcode',
    'Request ID (optional)',
  ];

  // Create data rows
  const dataRows = transfers.map((t) => [
    t.transferTo,
    t.transferMethod,
    t.currencyRecipientGets,
    t.currencyYouPay,
    t.amount,
    t.feePaidBy,
    t.accountName,
    t.bankAccountType,
    t.bankIdentifierType,
    t.bankIdentifierValue,
    t.accountNumber,
    t.transferPurpose,
    t.reference,
    t.description,
    t.recipientType,
    t.countryRegion,
    t.streetAddress,
    t.city,
    t.stateProvince,
    t.postcode,
    t.requestId || '',
  ]);

  const mainSheetData = [headers, ...dataRows];
  const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);

  // Set column widths for better readability
  mainSheet['!cols'] = [
    { wch: 26 }, // A: Transfer to
    { wch: 15 }, // B: Transfer method
    { wch: 24 }, // C: Currency recipient gets
    { wch: 16 }, // D: Currency you pay
    { wch: 35 }, // E: Transfer amount
    { wch: 12 }, // F: Fee paid by
    { wch: 50 }, // G: Account name
    { wch: 18 }, // H: Bank account type
    { wch: 20 }, // I: Bank identifier type
    { wch: 20 }, // J: Bank identifier value
    { wch: 18 }, // K: Account number
    { wch: 18 }, // L: Transfer purpose
    { wch: 15 }, // M: Reference
    { wch: 50 }, // N: Description
    { wch: 15 }, // O: Recipient type
    { wch: 26 }, // P: Country / region
    { wch: 25 }, // Q: Street address
    { wch: 18 }, // R: City
    { wch: 16 }, // S: State / Province
    { wch: 12 }, // T: Postcode
    { wch: 22 }, // U: Request ID
  ];

  // Apply header styling (bold, gray background, centered)
  const headerStyle = {
    font: { name: 'Arial', sz: 10, bold: true },
    fill: { fgColor: { rgb: '808080' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };

  // Apply styles to header row
  for (let col = 0; col < headers.length; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (mainSheet[cellRef]) {
      mainSheet[cellRef].s = headerStyle;
    }
  }

  // Add main sheet
  XLSX.utils.book_append_sheet(wb, mainSheet, 'Airwallex batch transfer');

  // Create Validation sheet (display values)
  const validationData = createValidationSheet();
  const validationSheet = XLSX.utils.aoa_to_sheet(validationData);
  XLSX.utils.book_append_sheet(wb, validationSheet, 'Validation');

  // Create ValidationValue sheet (code values)
  const validationValueData = createValidationValueSheet();
  const validationValueSheet = XLSX.utils.aoa_to_sheet(validationValueData);
  XLSX.utils.book_append_sheet(wb, validationValueSheet, 'ValidationValue');

  // Create DropdownListMapping sheet
  const dropdownData = createDropdownListMappingSheet();
  const dropdownSheet = XLSX.utils.aoa_to_sheet(dropdownData);
  XLSX.utils.book_append_sheet(wb, dropdownSheet, 'DropdownListMapping');

  // Create StorageUsedInternally sheet
  const storageData = [['version', 'v1']];
  const storageSheet = XLSX.utils.aoa_to_sheet(storageData);
  XLSX.utils.book_append_sheet(wb, storageSheet, 'StorageUsedInternally');

  // Hide the validation sheets
  if (!wb.Workbook) wb.Workbook = {};
  if (!wb.Workbook.Sheets) wb.Workbook.Sheets = [];
  wb.Workbook.Sheets = [
    { Hidden: 0, name: 'Airwallex batch transfer' },
    { Hidden: 1, name: 'Validation' },
    { Hidden: 1, name: 'ValidationValue' },
    { Hidden: 1, name: 'DropdownListMapping' },
    { Hidden: 1, name: 'StorageUsedInternally' },
  ];

  // Generate XLSX buffer
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// Column A: Countries for "Transfer to" dropdown (207 entries)
const VALIDATION_COUNTRIES_A = [
  'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla',
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Aruba', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belgium',
  'Belize', 'Benin', 'Bermuda', 'Bhutan', 'Bolivia (Plurinational State of)',
  'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei Darussalam', 'Bulgaria',
  'Burkina Faso', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Canary Islands',
  'Cayman Islands', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
  'Cook Islands', 'Costa Rica', 'Croatia', 'Curaçao', 'Cyprus', 'Czech Republic',
  "Côte d'Ivoire", 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Falkland Islands (Malvinas)', 'Faroe Islands', 'Fiji', 'Finland', 'France',
  'French Guiana', 'French Polynesia', 'French Southern Territories', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Gibraltar', 'Greece', 'Greenland',
  'Grenada', 'Guatemala', 'Guernsey', 'Guinea', 'Guinea Bissau', 'Guyana',
  'Holy See (Vatican City)', 'Honduras', 'Hong Kong SAR', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Ireland', 'Isle of Man', 'Israel', 'Italy', 'Jamaica',
  'Japan', 'Jersey', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Korea, Republic of',
  'Kosovo', 'Kuwait', 'Kyrgyzstan', "Lao People's Democratic Republic", 'Latvia',
  'Lebanon', 'Lesotho', 'Liberia', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Macao SAR', 'Macedonia, the former Yugoslav Republic of', 'Madagascar', 'Malawi',
  'Malaysia', 'Maldives', 'Malta', 'Marshall Islands', 'Martinique', 'Mauritania',
  'Mauritius', 'Mayotte', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Montserrat', 'Morocco', 'Namibia', 'Nepal', 'Netherlands',
  'New Caledonia', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'Northern Mariana Islands', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Palestine, State of', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Puerto Rico', 'Qatar', 'Republic of the Congo',
  'Romania', 'Rwanda', 'Réunion', 'Saint Barthélemy',
  'Saint Helena, Ascension and Tristan da Cunha', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Martin (French part)', 'Saint Pierre and Miquelon',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'South Africa', 'Spain', 'Sri Lanka',
  'Suriname', 'Sweden', 'Switzerland', 'Taiwan (China)', 'Tajikistan',
  'Tanzania, United Republic of', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turks and Caicos Islands', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
  'United States of America', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Viet Nam',
  'Virgin Islands (British)', 'Wallis and Futuna', 'Zambia', 'Zimbabwe',
];

// Column B: Transfer methods
const VALIDATION_TRANSFER_METHODS_B = ['ACH'];

// Column C: Currencies (27 entries)
const VALIDATION_CURRENCIES_C = [
  'AUD', 'BRL', 'CAD', 'CNY', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD', 'HUF', 'ILS',
  'JPY', 'KRW', 'MXN', 'NZD', 'NOK', 'PHP', 'PLN', 'RON', 'SGD', 'ZAR', 'SEK',
  'CHF', 'THB', 'AED', 'USD', 'VND',
];

// Column D: Fee paid by
const VALIDATION_FEE_PAID_BY_D = ['Payer', 'Recipient'];

// Column E: Bank account types
const VALIDATION_ACCOUNT_TYPES_E = ['Checking', 'Savings'];

// Column F: Bank identifier types
const VALIDATION_BANK_ID_TYPES_F = ['ACH routing number'];

// Column G: Transfer purposes (22 entries)
const VALIDATION_TRANSFER_PURPOSES_G = [
  'Audiovisual services', 'Bill payment', 'Business expenses', 'Construction',
  'Donation / charitable contribution', 'Education / training', 'Freight',
  'Goods purchased', 'Investment capital', 'Investment proceeds', 'Living expenses',
  'Loan / credit repayment', 'Medical Services', 'Pension',
  'Professional / Business Services', 'Real Estate', 'Taxes', 'Technical Services',
  'Transfer to own account', 'Travel', 'Wages / salary', 'Other Services',
];

// Column H: Countries for address (244 entries - more complete list)
const VALIDATION_COUNTRIES_H = [
  'Afghanistan', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola',
  'Anguilla', 'Antarctica', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Aruba',
  'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh',
  'Barbados', 'Belgium', 'Belize', 'Benin', 'Bermuda', 'Bhutan',
  'Bolivia (Plurinational State of)', 'Bonaire, Sint Eustatius and Saba',
  'Bosnia and Herzegovina', 'Botswana', 'Bouvet Island', 'Brazil',
  'British Indian Ocean Territory', 'Brunei Darussalam', 'Bulgaria', 'Burkina Faso',
  'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Canary Islands',
  'Cayman Islands', 'Central African Republic', 'Chad', 'Chile', 'China',
  'Christmas Island', 'Cocos (Keeling) Islands', 'Colombia', 'Comoros', 'Cook Islands',
  'Costa Rica', 'Croatia', 'Curaçao', 'Cyprus', 'Czech Republic', "Côte d'Ivoire",
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Falkland Islands (Malvinas)', 'Faroe Islands', 'Fiji', 'Finland', 'France',
  'French Guiana', 'French Polynesia', 'French Southern Territories', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Gibraltar', 'Greece', 'Greenland',
  'Grenada', 'Guadeloupe', 'Guam', 'Guatemala', 'Guernsey', 'Guinea', 'Guinea Bissau',
  'Guyana', 'Haiti', 'Heard Island and McDonald Islands', 'Holy See (Vatican City)',
  'Honduras', 'Hong Kong SAR', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iraq',
  'Ireland', 'Isle of Man', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jersey', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Korea, Republic of', 'Kosovo', 'Kuwait',
  'Kyrgyzstan', "Lao People's Democratic Republic", 'Latvia', 'Lebanon', 'Lesotho',
  'Liberia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macao SAR',
  'Macedonia, the former Yugoslav Republic of', 'Madagascar', 'Malawi', 'Malaysia',
  'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Martinique', 'Mauritania',
  'Mauritius', 'Mayotte', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Montserrat', 'Morocco', 'Mozambique', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'Netherlands Antilles', 'New Caledonia', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'Niue', 'Norfolk Island', 'Northern Mariana Islands', 'Norway',
  'Oman', 'Pakistan', 'Palau', 'Palestine, State of', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Philippines', 'Pitcairn', 'Poland', 'Portugal', 'Puerto Rico',
  'Qatar', 'Republic of the Congo', 'Romania', 'Rwanda', 'Réunion', 'Saint Barthélemy',
  'Saint Helena, Ascension and Tristan da Cunha', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Martin (French part)', 'Saint Pierre and Miquelon',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Sint Maarten (Dutch part)', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Georgia and the South Sandwich Islands', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Svalbard and Jan Mayen', 'Sweden',
  'Switzerland', 'Taiwan (China)', 'Tajikistan', 'Tanzania, United Republic of',
  'Thailand', 'The Democratic Republic Of The Congo', 'Timor-Leste', 'Togo', 'Tokelau',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Turks and Caicos Islands', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States Minor Outlying Islands', 'United States of America',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Venezuela', 'Viet Nam',
  'Virgin Islands (British)', 'Virgin Islands (U.S.)', 'Wallis and Futuna',
  'Western Sahara', 'Yemen', 'Zambia', 'Zimbabwe', 'Åland Islands',
];

// Column I: US States (57 entries)
const VALIDATION_US_STATES_I = [
  'Alabama', 'Alaska', 'American Samoa', 'Arizona', 'Arkansas', 'California',
  'Colorado', 'Connecticut', 'Delaware', 'District of Columbia', 'Florida',
  'Georgia', 'Guam', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
  'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
  'North Dakota', 'Northern Mariana Islands', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Puerto Rico', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'United States Minor Outlying Islands', 'Utah', 'Vermont',
  'Virgin Islands, U.S.', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin',
  'Wyoming',
];

// ValidationValue Column A: Country codes (207 entries)
const VALIDATION_VALUE_COUNTRY_CODES_A = [
  'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AG', 'AR', 'AM', 'AW', 'AU', 'AT', 'AZ',
  'BS', 'BH', 'BD', 'BB', 'BE', 'BZ', 'BJ', 'BM', 'BT', 'BO', 'BA', 'BW', 'BR',
  'BN', 'BG', 'BF', 'CV', 'KH', 'CM', 'CA', 'IC', 'KY', 'CF', 'TD', 'CL', 'CN',
  'CO', 'CK', 'CR', 'HR', 'CW', 'CY', 'CZ', 'CI', 'DK', 'DJ', 'DM', 'DO', 'EC',
  'EG', 'SV', 'GQ', 'EE', 'SZ', 'ET', 'FK', 'FO', 'FJ', 'FI', 'FR', 'GF', 'PF',
  'TF', 'GA', 'GM', 'GE', 'DE', 'GH', 'GI', 'GR', 'GL', 'GD', 'GT', 'GG', 'GN',
  'GW', 'GY', 'VA', 'HN', 'HK', 'HU', 'IS', 'IN', 'ID', 'IE', 'IM', 'IL', 'IT',
  'JM', 'JP', 'JE', 'JO', 'KZ', 'KE', 'KI', 'KR', 'XK', 'KW', 'KG', 'LA', 'LV',
  'LB', 'LS', 'LR', 'LI', 'LT', 'LU', 'MO', 'MK', 'MG', 'MW', 'MY', 'MV', 'MT',
  'MH', 'MQ', 'MR', 'MU', 'YT', 'MX', 'FM', 'MD', 'MC', 'MN', 'ME', 'MS', 'MA',
  'NA', 'NP', 'NL', 'NC', 'NZ', 'NI', 'NE', 'NG', 'MP', 'NO', 'OM', 'PK', 'PW',
  'PS', 'PA', 'PG', 'PY', 'PE', 'PH', 'PL', 'PT', 'PR', 'QA', 'CG', 'RO', 'RW',
  'RE', 'BL', 'SH', 'KN', 'LC', 'MF', 'PM', 'VC', 'WS', 'SM', 'ST', 'SA', 'SN',
  'RS', 'SC', 'SL', 'SG', 'SK', 'SI', 'SB', 'ZA', 'ES', 'LK', 'SR', 'SE', 'CH',
  'TW', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TO', 'TT', 'TN', 'TR', 'TC', 'TV', 'UG',
  'UA', 'AE', 'GB', 'US', 'UY', 'UZ', 'VU', 'VN', 'VG', 'WF', 'ZM', 'ZW',
];

// ValidationValue Column B: Transfer method codes
const VALIDATION_VALUE_TRANSFER_METHODS_B = ['ACH'];

// ValidationValue Column C: Currency codes (27 entries)
const VALIDATION_VALUE_CURRENCIES_C = [
  'AUD', 'BRL', 'CAD', 'CNY', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD', 'HUF', 'ILS',
  'JPY', 'KRW', 'MXN', 'NZD', 'NOK', 'PHP', 'PLN', 'RON', 'SGD', 'ZAR', 'SEK',
  'CHF', 'THB', 'AED', 'USD', 'VND',
];

// ValidationValue Column D: Fee paid by codes
const VALIDATION_VALUE_FEE_PAID_BY_D = ['PAYER', 'BENEFICIARY'];

// ValidationValue Column E: Account type codes
const VALIDATION_VALUE_ACCOUNT_TYPES_E = ['Checking', 'Savings'];

// ValidationValue Column F: Bank ID type codes
const VALIDATION_VALUE_BANK_ID_TYPES_F = ['aba'];

// ValidationValue Column G: Transfer purpose codes (22 entries)
const VALIDATION_VALUE_PURPOSE_CODES_G = [
  'audio_visual_services', 'bill_payment', 'business_expenses', 'construction',
  'donation_charitable_contribution', 'education_training', 'freight',
  'goods_purchased', 'investment_capital', 'investment_proceeds', 'living_expenses',
  'loan_credit_repayment', 'medical_services', 'pension',
  'professional_business_services', 'real_estate', 'taxes', 'technical_services',
  'transfer_to_own_account', 'travel', 'wages_salary', 'other_services',
];

// ValidationValue Column H: Country codes for address (244 entries)
const VALIDATION_VALUE_COUNTRY_CODES_H = [
  'AF', 'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AQ', 'AG', 'AR', 'AM', 'AW', 'AU',
  'AT', 'AZ', 'BS', 'BH', 'BD', 'BB', 'BE', 'BZ', 'BJ', 'BM', 'BT', 'BO', 'BQ',
  'BA', 'BW', 'BV', 'BR', 'IO', 'BN', 'BG', 'BF', 'BI', 'CV', 'KH', 'CM', 'CA',
  'IC', 'KY', 'CF', 'TD', 'CL', 'CN', 'CX', 'CC', 'CO', 'KM', 'CK', 'CR', 'HR',
  'CW', 'CY', 'CZ', 'CI', 'DK', 'DJ', 'DM', 'DO', 'EC', 'EG', 'SV', 'GQ', 'ER',
  'EE', 'SZ', 'ET', 'FK', 'FO', 'FJ', 'FI', 'FR', 'GF', 'PF', 'TF', 'GA', 'GM',
  'GE', 'DE', 'GH', 'GI', 'GR', 'GL', 'GD', 'GP', 'GU', 'GT', 'GG', 'GN', 'GW',
  'GY', 'HT', 'HM', 'VA', 'HN', 'HK', 'HU', 'IS', 'IN', 'ID', 'IQ', 'IE', 'IM',
  'IL', 'IT', 'JM', 'JP', 'JE', 'JO', 'KZ', 'KE', 'KI', 'KR', 'XK', 'KW', 'KG',
  'LA', 'LV', 'LB', 'LS', 'LR', 'LI', 'LT', 'LU', 'MO', 'MK', 'MG', 'MW', 'MY',
  'MV', 'ML', 'MT', 'MH', 'MQ', 'MR', 'MU', 'YT', 'MX', 'FM', 'MD', 'MC', 'MN',
  'ME', 'MS', 'MA', 'MZ', 'NA', 'NR', 'NP', 'NL', 'AN', 'NC', 'NZ', 'NI', 'NE',
  'NG', 'NU', 'NF', 'MP', 'NO', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE',
  'PH', 'PN', 'PL', 'PT', 'PR', 'QA', 'CG', 'RO', 'RW', 'RE', 'BL', 'SH', 'KN',
  'LC', 'MF', 'PM', 'VC', 'WS', 'SM', 'ST', 'SA', 'SN', 'RS', 'SC', 'SL', 'SG',
  'SX', 'SK', 'SI', 'SB', 'SO', 'ZA', 'GS', 'SS', 'ES', 'LK', 'SD', 'SR', 'SJ',
  'SE', 'CH', 'TW', 'TJ', 'TZ', 'TH', 'CD', 'TL', 'TG', 'TK', 'TO', 'TT', 'TN',
  'TR', 'TM', 'TC', 'TV', 'UG', 'UA', 'AE', 'GB', 'UM', 'US', 'UY', 'UZ', 'VU',
  'VE', 'VN', 'VG', 'VI', 'WF', 'EH', 'YE', 'ZM', 'ZW', 'AX',
];

// ValidationValue Column I: US State codes (57 entries)
const VALIDATION_VALUE_US_STATE_CODES_I = [
  'US-AL', 'US-AK', 'US-AS', 'US-AZ', 'US-AR', 'US-CA', 'US-CO', 'US-CT', 'US-DE',
  'US-DC', 'US-FL', 'US-GA', 'US-GU', 'US-HI', 'US-ID', 'US-IL', 'US-IN', 'US-IA',
  'US-KS', 'US-KY', 'US-LA', 'US-ME', 'US-MD', 'US-MA', 'US-MI', 'US-MN', 'US-MS',
  'US-MO', 'US-MT', 'US-NE', 'US-NV', 'US-NH', 'US-NJ', 'US-NM', 'US-NY', 'US-NC',
  'US-ND', 'US-MP', 'US-OH', 'US-OK', 'US-OR', 'US-PA', 'US-PR', 'US-RI', 'US-SC',
  'US-SD', 'US-TN', 'US-TX', 'US-UM', 'US-UT', 'US-VT', 'US-VI', 'US-VA', 'US-WA',
  'US-WV', 'US-WI', 'US-WY',
];

function createValidationSheet(): any[][] {
  // Build the sheet data - 9 columns matching the template exactly
  const maxRows = Math.max(
    VALIDATION_COUNTRIES_A.length,
    VALIDATION_TRANSFER_METHODS_B.length,
    VALIDATION_CURRENCIES_C.length,
    VALIDATION_FEE_PAID_BY_D.length,
    VALIDATION_ACCOUNT_TYPES_E.length,
    VALIDATION_BANK_ID_TYPES_F.length,
    VALIDATION_TRANSFER_PURPOSES_G.length,
    VALIDATION_COUNTRIES_H.length,
    VALIDATION_US_STATES_I.length
  );

  const data: any[][] = [];
  for (let i = 0; i < maxRows; i++) {
    data.push([
      VALIDATION_COUNTRIES_A[i] || '',
      VALIDATION_TRANSFER_METHODS_B[i] || '',
      VALIDATION_CURRENCIES_C[i] || '',
      VALIDATION_FEE_PAID_BY_D[i] || '',
      VALIDATION_ACCOUNT_TYPES_E[i] || '',
      VALIDATION_BANK_ID_TYPES_F[i] || '',
      VALIDATION_TRANSFER_PURPOSES_G[i] || '',
      VALIDATION_COUNTRIES_H[i] || '',
      VALIDATION_US_STATES_I[i] || '',
    ]);
  }

  return data;
}

function createValidationValueSheet(): any[][] {
  // Build the sheet data - 9 columns with code values
  const maxRows = Math.max(
    VALIDATION_VALUE_COUNTRY_CODES_A.length,
    VALIDATION_VALUE_TRANSFER_METHODS_B.length,
    VALIDATION_VALUE_CURRENCIES_C.length,
    VALIDATION_VALUE_FEE_PAID_BY_D.length,
    VALIDATION_VALUE_ACCOUNT_TYPES_E.length,
    VALIDATION_VALUE_BANK_ID_TYPES_F.length,
    VALIDATION_VALUE_PURPOSE_CODES_G.length,
    VALIDATION_VALUE_COUNTRY_CODES_H.length,
    VALIDATION_VALUE_US_STATE_CODES_I.length
  );

  const data: any[][] = [];
  for (let i = 0; i < maxRows; i++) {
    data.push([
      VALIDATION_VALUE_COUNTRY_CODES_A[i] || '',
      VALIDATION_VALUE_TRANSFER_METHODS_B[i] || '',
      VALIDATION_VALUE_CURRENCIES_C[i] || '',
      VALIDATION_VALUE_FEE_PAID_BY_D[i] || '',
      VALIDATION_VALUE_ACCOUNT_TYPES_E[i] || '',
      VALIDATION_VALUE_BANK_ID_TYPES_F[i] || '',
      VALIDATION_VALUE_PURPOSE_CODES_G[i] || '',
      VALIDATION_VALUE_COUNTRY_CODES_H[i] || '',
      VALIDATION_VALUE_US_STATE_CODES_I[i] || '',
    ]);
  }

  return data;
}

function createDropdownListMappingSheet(): any[][] {
  return [[
    'beneficiary.bankDetails.bankCountryCode',
    'US-USD-COMPANY-ACH',
    'US-USD-ACH-COMPANY.sourceCurrency',
    'US-USD-ACH-COMPANY.feePaidBy',
    'US-USD-ACH-COMPANY.beneficiary.bankDetails.bankAccountCategory',
    'US-USD-ACH-COMPANY.beneficiary.bankDetails.accountRoutingType1-ACH routing number',
    'US-USD-ACH-COMPANY.reason',
    'beneficiary.address.countryCode',
    'US-USD-ACH-COMPANY.beneficiary.address.state',
  ]];
}

/**
 * Downloads the Airwallex XLSX file
 */
export function downloadAirwallexXLSX(
  transfers: AirwallexTransferRow[],
  filename: string,
  batchDate: Date = new Date()
): void {
  const blob = generateAirwallexXLSX(transfers, batchDate);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
